const JavelinaBaseClient = require("./JavelinaBaseClient");

class JavelinaMemberClient extends JavelinaBaseClient {
    constructor(
        javelinaProxyApiUrl = process.env.JAVELINA_PROXY_API_URL, 
        javelinaProxySecretName = process.env.JAVELINA_PROXY_SECRET_NAME || '',
        axiosOptions
    ) {
        super(javelinaProxyApiUrl, javelinaProxySecretName, axiosOptions)
    }

    /**
     * @param {*} jvMemberDetails 
     * @param {*} dob 
     * @param {*} fName 
     * @param {*} lName 
     * @param {*} meta 
     */
    verifyJvMember(jvMemberDetails, dob, fName, lName, meta) {
        for (let memberDet of jvMemberDetails) {
            if (memberDet.PatientFirstName.toLowerCase() === fName.toLowerCase() &&
                memberDet.PatientLastName.toLowerCase() === lName.toLowerCase() &&
                memberDet.BirthDate === dob) {
                return { Response: memberDet, MetaData: meta }
            }
        }
        return null
    }

    /**
     * @description returns a family of JV members, or specific member, based on the certificate number and membr info
     * @param {String} certificateNumber the family certificate number
     * @param {String} firstName first name
     * @param {String} lastName last name
     * @param {String} dateOfBirth formatt YYYY-MM-DD
     */
    async searchMembers(certificateNumber, firstName = null, lastName = null, dateOfBirth = null) {
        await this.createSecureAxiosInstance()

        const params = {
            certificateNumber: certificateNumber
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/eligibility/searchMembers`);

        const response = await this.secureAxiosInstance.get(path, {
            validateStatus: function (status) {
                return (
                    (status >= 200 && status < 300)
                    || (status === 404)
                );
            }
        });

        if (response.status !== 200) {
            return null;
        }

        if (dateOfBirth && firstName && lastName && response.data) {
            return this.verifyJvMember(response.data.Response.MemberDetails, dateOfBirth, firstName, lastName, response.data.MetaData);
        }

        return response.data;
    }

    /**
     * @description return single employee data.
     * @param {*} employeeId 
     * @param {*} depSeqNumber
     */
    async getMember(employeeId, depSeqNumber = null) {
        await this.createSecureAxiosInstance()
        
        const params = {
            EmployeeID: employeeId.toString(),
            DepSequenceNumber: depSeqNumber ? depSeqNumber.toString() : null
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/eligibility/members`);

        const response = await this.secureAxiosInstance.get(path, {
            validateStatus: function (status) {
                return (
                    (status >= 200 && status < 300)
                    || (status === 404)
                );
            }
        });

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description adds a covg slice for an employee
     */
    async addMemberCoverageSlice(covg, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "AddMemberEligibility": {
                ...covg
            }
        }

        const response = await this.secureAxiosInstance.post(`/pureProxy/eligibility/addMemberEligibility`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description adds a covg slice for a dependent
     */
    async addDependentCoverageSlice(covg, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "AddDependentEligibility": {
                ...covg
            }
        }

        const response = await this.secureAxiosInstance.post(`/pureProxy/eligibility/addDependentEligibility`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description adds a covg slice for an employee
     */
    async updateMemberCoverageSlice(covg, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "UpdateMemberEligibility": {
                ...covg
            }
        }

        const response = await this.secureAxiosInstance.put(`/pureProxy/eligibility/updateMemberEligibility`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description adds a covg slice for an employee
     */
    async updateDependentCoverageSlice(covg, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "UpdateDependentEligibility": {
                ...covg
            }
        }

        const response = await this.secureAxiosInstance.put(`/pureProxy/eligibility/updateDependentEligibility`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description gets coverage for a member
     * @param {String} certificateNumber the family certificate number
     */
    async getCoverage(employeeId, depSeqNumber = null, effDate) {
        await this.createSecureAxiosInstance();

        const params = {
            EmployeeID: employeeId.toString(),
            DepSequenceNumber: depSeqNumber ? depSeqNumber.toString() : null,
            EffectiveDate: effDate
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/eligibility/memberCoverages`);

        const response = await this.secureAxiosInstance.get(path);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description adds a pcp to a covg slice
     */
    async addDependentPcp(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "AddDependentPrimaryCareProvider": {
                ...body
            }
        }
        const response = await this.secureAxiosInstance.post(`pureProxy/eligibility/addDependentPrimaryCareProvider`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description adds a pcp to a covg slice
     */
    async addEmployeePcp(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "AddMemberPrimaryCareProvider": {
                ...body
            }
        }

        const response = await this.secureAxiosInstance.post(`pureProxy/eligibility/addMemberPrimaryCareProvider`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description updates a pcp on a covg slice
     */
    async updateDependentPcp(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }
        
        let req = {
            "UpdateDependentPrimaryCareProvider": {
                ...body
            }
        }
        const response = await this.secureAxiosInstance.put(`pureProxy/eligibility/updateDependentPrimaryCareProvider`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description updates a pcp on a covg slice
     */
    async updateEmployeePcp(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }
        let req = {
            "UpdateMemberPrimaryCareProvider": {
                ...body
            }
        }
        const response = await this.secureAxiosInstance.put(`pureProxy/eligibility/updateMemberPrimaryCareProvider`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description deletes a pcp from a covg slice
     */
    async deleteDependentPcp(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId){
            throw "transaction Id required";
        }

        const path = "/employeeID/" + body.EmployeeID + "/depSequenceNumber/" + body.DepSequenceNumber + "/effectiveDate/" + body.EffectiveDate + "/product/" + body.Product + "/connectorID/" + body.ConnectorID;
        const response = await this.secureAxiosInstance.delete(`pureProxy/eligibility/deleteDependentPrimaryCareProvider${path}`);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description deletes a pcp from a covg slice
     */
    async deleteEmployeePcp(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId){
            throw "transaction Id required";
        }

        const path = "/employeeID/" + body.EmployeeID + "/effectiveDate/" + body.EffectiveDate + "/product/" + body.Product + "/connectorID/" + body.ConnectorID;
        const response = await this.secureAxiosInstance.delete(`pureProxy/eligibility/deleteMemberPrimaryCareProvider${path}`);
        
        if (response.status !== 200) {
            return null;
        }
        
        return response.data;
    }

    /**
     * @description updates an employee record
     */
    async updateEmployee(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "UpdateMember": {
                ...body
            }
        }

        const response = await this.secureAxiosInstance.put(`pureProxy/eligibility/updateMember`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description updates a dependent record
     */
    async updateDependent(body, txnId) {
        await this.createSecureAxiosInstance({'X-Transaction-ID': txnId})

        if (!txnId) {
            throw "transaction Id required"
        }

        let req = {
            "UpdateDependent": {
                ...body
            }
        }

        const response = await this.secureAxiosInstance.put(`pureProxy/eligibility/updateDependent`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
    * @param {*} blockId 
    * @param {*} clientId 
    */
    async getMembersByBlockIdAndClientId(blockId, clientId) {
        this.createSecureAxiosInstance()

        const params = {
            BlkOfBusID: blockId,
            ClientID: clientId
        }

        let path = this.queryParamsToPathParams(params, `pureProxy/eligibility/SearchMembers`);

        try {
            const response = await this.secureAxiosInstance.get(path);

            if (response.status !== 200) {
                return null;
            }

            return response.data;
        } catch (e) {
            return null;
        }
    }

    async getAllMembersByBlockIdAndClientId(blockId, clientId) {
        const response = await this.getMembersByBlockIdAndClientId(blockId, clientId);
        const results = await this.loopPaginationResults("MemberDetails", response);
        return results;
    }

    /**
     * @description get all clients members
     */
    async getAllMembers(batchSize = 10) {
        const client = new JavelinaClientClient(this.url, this.secretName)

        const response = await client.getAllClients();

        if (response.Response == null || response.Response.Client == null || response.Response.Client.length < 1) {
            return [];
        }

        let allClients = [];

        for (let item of response.Response.Client) {
            allClients.push({
                blockId: item.BlockOfBusiness.BlkOfBusID,
                clientId: item.ClientID
            });
        }

        let batch = [];
        let results = [];
        for (let i = 0; i < allClients.length; i++) {
            batch.push(getAllMembersByBlockIdAndClientId(allClients[i].blockId, allClients[i].clientId));

            if (batch.length === batchSize || i === allClients.length - 1) {
                let batchResults = await Promise.all(batch);
                results.push(...batchResults)
                batch = [];
            }
        }

        let flatResults = [];
        for (let item of results) {
            flatResults.push(...item);
        }

        return flatResults;
    }

    /**
     * @description add member
     */
    async addEmployee(body) {
        await this.createSecureAxiosInstance();

        let req = {
            "AddMember": {
                ...body
            }
        }

        const response = await this.secureAxiosInstance.post(`pureProxy/eligibility/addMember`, req);

        if (response.status !== 200) {
            return null;
        }
        
        return response.data;
    }

    /**
     * @description addDependent
     */
    async addDependent(body) {
        await this.createSecureAxiosInstance();

        let req = {
            "AddDependent": {
                ...body
            }
        }

        const response = await this.secureAxiosInstance.post(`pureProxy/eligibility/addDependent`, req);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description returns the ID card document for a member
     * @param {*} employeeId the employee ID (member ID)
     */
    async getIdCard(employeeId) {
        await this.createSecureAxiosInstance({ 'accept': 'application/octet-stream', responseType: 'arraybuffer' })

        const params = {
            employeeID: employeeId.toString(),
        };

        const path = this.queryParamsToPathParams(params, `pureProxy/document/getIdCard`);

        const response = await this.secureAxiosInstance.get(path);

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @description returns the member employee ID for a member
     * @param member member data from dynamo
     */
    async getEmployeeIdFromMember(member) {
        if (!member){
            return null;
        }

        if (member.employeeId){
            return member.employeeId;
        }

        if (member.certificateNumber){
            const memberSearch = await this.searchMembers(member.certificateNumber);
            if (memberSearch && memberSearch.Response && memberSearch.Response.MemberDetails){
                return memberSearch.Response.MemberDetails[0].EmployeeID;;
            }
        }

        return null;
    }
}

module.exports = JavelinaMemberClient